import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { Modal, Btn, LoadingSpinner } from '@/presentation/component/ui';
import { usePayment } from '@/presentation/hook/usePayment';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { StripePaymentForm } from './StripePaymentForm';
import { PaymentSummary } from './PaymentSummary';
import type { Subcategory } from '@/domain/model/Subcategory';

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  subcategory: Subcategory;
}

export const PaymentModal = ({ open, onClose, subcategory }: PaymentModalProps) => {
  const { t } = useTranslation();
  const {
    availableMethods,
    currentIntent,
    step,
    selectedProvider,
    isLoading,
    error,
    fetchAvailableMethods,
    createIntent,
    setStep,
    setSelectedProvider,
    clearIntent,
    clearError,
    reset,
  } = usePayment();

  const [paymentError, setPaymentError] = useState<string | null>(null);

  useEffect(() => {
    if (open && availableMethods.length === 0) {
      fetchAvailableMethods();
    }
  }, [open, availableMethods.length, fetchAvailableMethods]);

  useEffect(() => {
    if (open) {
      reset();
      setPaymentError(null);
    }
  }, [open, reset]);

  const handleContinueToPayment = useCallback(async () => {
    if (!selectedProvider) return;

    clearError();
    setPaymentError(null);

    try {
      const intent = await createIntent({
        subcategoryId: subcategory.id,
        paymentProvider: selectedProvider,
        currency: 'EUR',
        returnUrl: `${window.location.origin}/payment/success`,
        cancelUrl: `${window.location.origin}/payment/cancel`,
      });

      if (selectedProvider === 'stripe') {
        setStep('payment-form');
      } else {
        const approvalUrl = intent.providerData?.approval_url;
        if (typeof approvalUrl === 'string' && approvalUrl) {
          window.location.href = approvalUrl;
        } else {
          setStep('payment-form');
        }
      }
    } catch {
      // error already set by store
    }
  }, [selectedProvider, subcategory.id, createIntent, clearError, setStep]);

  const handlePaymentSuccess = useCallback(
    (clientSecret: string) => {
      const params = new URLSearchParams({
        payment_intent_client_secret: clientSecret,
        subcategory: subcategory.name,
        subcategory_id: subcategory.id,
      });
      window.location.href = `/payment/success?${params.toString()}`;
    },
    [subcategory.id, subcategory.name],
  );

  const handlePaymentError = useCallback(
    (message: string) => {
      setPaymentError(message);
      setStep('error');
    },
    [setStep],
  );

  const handleBack = useCallback(() => {
    setStep('method-selection');
    clearIntent();
    setPaymentError(null);
  }, [setStep, clearIntent]);

  const handleRetry = useCallback(() => {
    clearError();
    setPaymentError(null);
    setStep('method-selection');
  }, [clearError, setStep]);

  const preventClose = step === 'processing' ? () => {} : onClose;

  const renderContent = () => {
    switch (step) {
      case 'method-selection':
        return (
          <div className="space-y-6">
            <PaymentMethodSelector
              availableMethods={availableMethods}
              selectedProvider={selectedProvider}
              onSelect={setSelectedProvider}
              isLoading={isLoading}
            />
            <PaymentSummary subcategory={subcategory} />
            <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-line)]">
              <Btn kind="ghost" onClick={onClose}>
                {t('buttons.cancel')}
              </Btn>
              <Btn
                kind="primary"
                onClick={handleContinueToPayment}
                disabled={!selectedProvider || isLoading}
                isLoading={isLoading}
              >
                {t('payment.continueToPayment')}
              </Btn>
            </div>
          </div>
        );

      case 'payment-form':
        if (selectedProvider === 'stripe' && currentIntent) {
          return (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBack}
                  className="p-1 hover:bg-[var(--color-surface-2)] rounded"
                  aria-label={t('payment.back')}
                >
                  <ArrowLeft className="w-4 h-4 text-[var(--color-ink)]" />
                </button>
                <h3 className="text-lg font-medium text-[var(--color-ink)]">
                  {t('payment.enterPaymentDetails')}
                </h3>
              </div>
              <StripePaymentForm
                paymentIntent={currentIntent}
                price={subcategory.price}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </div>
          );
        }
        return null;

      case 'processing':
        return (
          <div className="text-center py-8">
            <LoadingSpinner />
            <h3 className="text-lg font-medium text-[var(--color-ink)] mb-2 mt-4">
              {t('payment.processingPayment')}
            </h3>
            <p className="text-[var(--color-ink-soft)]">{t('payment.pleaseWait')}</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-[var(--color-good)] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[var(--color-ink)] mb-2">
              {t('payment.paymentSuccessful')}
            </h3>
            <p className="text-[var(--color-ink-soft)] mb-4">{t('payment.accessGranted')}</p>
            <p className="text-sm text-[var(--color-ink-faint)]">{t('payment.redirectingSoon')}</p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-8">
            <XCircle className="w-16 h-16 text-[var(--color-bad)] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[var(--color-ink)] mb-2">
              {t('payment.paymentFailed')}
            </h3>
            <p className="text-[var(--color-bad)] mb-6">
              {paymentError || error || t('payment.genericError')}
            </p>
            <div className="flex justify-center gap-3">
              <Btn kind="ghost" onClick={onClose}>
                {t('buttons.cancel')}
              </Btn>
              <Btn kind="primary" onClick={handleRetry}>
                {t('buttons.retry')}
              </Btn>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={preventClose} title={t('payment.purchaseTitle')} size="lg">
      {renderContent()}
    </Modal>
  );
};
